import { Badge } from '../ui/Badge';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PaymentStatus = 'pending' | 'approved' | 'rejected';

interface StatusMessageProps {
  status: PaymentStatus;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  PaymentStatus,
  { message: string; badgeVariant: 'warning' | 'success' | 'danger'; icon: string; bgClass: string; textClass: string; borderClass: string }
> = {
  pending: {
    message: 'Payment under review',
    badgeVariant: 'warning',
    icon: '🕐',
    bgClass: 'bg-yellow-50',
    textClass: 'text-yellow-800',
    borderClass: 'border-yellow-200',
  },
  approved: {
    message: 'Payment approved - Subscription activated',
    badgeVariant: 'success',
    icon: '✅',
    bgClass: 'bg-green-50',
    textClass: 'text-green-800',
    borderClass: 'border-green-200',
  },
  rejected: {
    message: 'Payment rejected - Please contact support',
    badgeVariant: 'danger',
    icon: '❌',
    bgClass: 'bg-red-50',
    textClass: 'text-red-800',
    borderClass: 'border-red-200',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const StatusMessage = ({ status }: StatusMessageProps) => {
  const config = STATUS_CONFIG[status];

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-4 ${config.bgClass} ${config.borderClass}`}
      role="status"
      aria-live="polite"
    >
      <span className="text-xl" aria-hidden="true">{config.icon}</span>
      <div className="flex items-center gap-2">
        <Badge variant={config.badgeVariant}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
        <span className={`text-sm font-medium ${config.textClass}`}>
          {config.message}
        </span>
      </div>
    </div>
  );
};

export default StatusMessage;
